import React, { useEffect, useState } from "react";
import { Loader2, FileText, Target, Compass, Save, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";

const LIMITS = { vision: 20000, mission: 20000, about_institute: 60000 };
const EMPTY = { vision: "", mission: "", about_institute: "" };

// Strip HTML for character counts so editors see the real length, not tag noise.
const stripHtml = (html) => {
  if (!html) return "";
  if (typeof document === "undefined") return html;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

export default function InstituteInfo() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [form, setForm] = useState(EMPTY);
  const [serverState, setServerState] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/info-content");
      const next = {
        vision: data.vision || "",
        mission: data.mission || "",
        about_institute: data.about_institute || "",
      };
      setForm(next);
      setServerState(next);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty =
    form.vision !== serverState.vision ||
    form.mission !== serverState.mission ||
    form.about_institute !== serverState.about_institute;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        vision: form.vision,
        mission: form.mission,
        about_institute: form.about_institute,
      };
      const { data } = await api.put("/institute/info-content", payload);
      const next = {
        vision: data.vision || "",
        mission: data.mission || "",
        about_institute: data.about_institute || "",
      };
      setServerState(next);
      setForm(next);
      toast.success("Institute information saved. Public website will refresh on next visit.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setForm(serverState);

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center text-[#234333]">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl" data-testid="institute-info-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
            Website Updates
          </div>
          <h1 className="font-serif text-3xl text-[#1c1a17]">Institute Information</h1>
          <p className="mt-1 text-sm text-[#6b655b] max-w-2xl">
            Manage Vision, Mission, and About Institute content. Use formatting (headings, lists, links, emphasis) — it will render
            on the institute&apos;s public website (About and Vision &amp; Mission pages) as soon as you save.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {dirty && (
              <button
                type="button"
                onClick={reset}
                disabled={saving}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#6b655b] hover:text-[#1c1a17] px-3 py-2 rounded-sm border border-[#e5e0d8] hover:bg-[#f9f8f6] disabled:opacity-50"
                data-testid="info-reset-btn"
              >
                <RotateCcw size={13} /> Reset
              </button>
            )}
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider bg-[#234333] hover:bg-[#1a3325] disabled:bg-[#9aa39e] text-white font-semibold px-4 py-2 rounded-sm shadow-sm transition-colors"
              data-testid="info-save-btn"
            >
              {saving ? (<><Loader2 className="animate-spin" size={13} /> Saving…</>) : (<><Save size={13} /> Save Changes</>)}
            </button>
          </div>
        )}
      </div>

      {!canEdit && (
        <div className="mb-4 bg-[#FFF1E5] border border-[#FFD9A8] text-[#7a3a07] rounded-sm px-4 py-3 text-sm">
          Only Institute Admins can edit this content. You can view the current information below.
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <FieldCard
          icon={Target}
          accent="emerald"
          title="Vision"
          help="A short, aspirational statement of where the institute aims to be."
          value={form.vision}
          maxLen={LIMITS.vision}
          disabled={!canEdit || saving}
          onChange={(v) => set("vision", v)}
          testid="info-field-vision"
          placeholder="e.g., To be a globally recognised centre of excellence in engineering education and applied research…"
          canEdit={canEdit}
        />
        <FieldCard
          icon={Compass}
          accent="orange"
          title="Mission"
          help="The actions, commitments, and how-you'll-get-there for the vision above."
          value={form.mission}
          maxLen={LIMITS.mission}
          disabled={!canEdit || saving}
          onChange={(v) => set("mission", v)}
          testid="info-field-mission"
          placeholder="e.g., Deliver an outcome-based curriculum, foster applied research, cultivate ethical engineers…"
          canEdit={canEdit}
        />
        <FieldCard
          icon={FileText}
          accent="navy"
          title="About Institute"
          help="The longer 'About the Campus' write-up shown on the public About page."
          value={form.about_institute}
          maxLen={LIMITS.about_institute}
          fullWidth
          disabled={!canEdit || saving}
          onChange={(v) => set("about_institute", v)}
          testid="info-field-about"
          placeholder="Tell visitors about the institute — its history, programmes, ethos, campus, and what makes it distinctive…"
          canEdit={canEdit}
        />
      </div>
    </div>
  );
}

function FieldCard({ icon: Icon, accent, title, help, value, maxLen, fullWidth = false, disabled, onChange, testid, placeholder, canEdit }) {
  const acc =
    accent === "orange"
      ? { chip: "bg-[#FFE9D6] text-[#C24A00]", icon: "bg-[#FFE9D6] text-[#C24A00]" }
      : accent === "navy"
      ? { chip: "bg-[#DBEAFE] text-[#1E3A8A]", icon: "bg-[#DBEAFE] text-[#1E3A8A]" }
      : { chip: "bg-[#D1FAE5] text-[#065F46]", icon: "bg-[#D1FAE5] text-[#065F46]" };
  const plainLen = stripHtml(value).length;
  return (
    <div className={`bg-white rounded-sm border border-[#e5e0d8] p-5 ${fullWidth ? "lg:col-span-2" : ""}`} data-testid={`${testid}-card`}>
      <div className="flex items-center gap-3 mb-3">
        <span className={`w-10 h-10 rounded-md ${acc.icon} flex items-center justify-center`}>
          <Icon size={18} strokeWidth={1.6} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg text-[#1c1a17] leading-tight">{title}</div>
          <div className="text-[11px] text-[#6b655b]">{help}</div>
        </div>
        <span className={`shrink-0 text-[10px] tracking-wider uppercase font-bold px-2 py-1 rounded ${acc.chip}`}>
          {plainLen} chars
        </span>
      </div>
      {canEdit ? (
        <RichTextEditor
          value={value}
          onChange={(html) => {
            if (html && html.length > maxLen) return;
            onChange(html);
          }}
          placeholder={placeholder}
          testid={testid}
        />
      ) : (
        <div
          className="prose prose-sm max-w-none min-h-[140px] px-3 py-2.5 bg-[#f9f8f6] border border-[#e5e0d8] rounded-sm"
          data-testid={`${testid}-readonly`}
          dangerouslySetInnerHTML={{ __html: value || "<em>(empty)</em>" }}
        />
      )}
    </div>
  );
}
